import { sentryAddAppLoadBreadcrumb, sentryCaptureAppLoadDiagnostic } from '@/utils/sentry';

export const AppBundle = {
  Journeys: 'journeys',
} as const;

export type AppBundle = (typeof AppBundle)[keyof typeof AppBundle];

export const AppBundleRoutes = {
  [AppBundle.Journeys]: '/journeys',
};

export const AppBundleNames = {
  [AppBundle.Journeys]: 'Journeys',
};

function isUnderRoute(pathname: string, route: string) {
  return pathname === route || pathname.startsWith(`${route}/`);
}

type JourneysPreloadSkipContext = {
  pathname: string;
  hasPersistedSession: boolean;
};

/**
 * Whether a Journeys preload started on this page could never be consumed, because the page always
 * leaves via a full-document navigation that discards `window.__hydra_preload_app_bundles__`.
 * Preloading anyway only burns bandwidth and turns ordinary fetch cancellation into
 * `preload_rejected` noise.
 *
 * `/logout` always qualifies — it ends in `hardRedirect('/login')`.
 *
 * `/login` only qualifies when the visitor actually logs in (`hardRedirect` in `login-flow.tsx`). An
 * already-authenticated visit — a bookmarked login, or recovery from a rejected masquerade — instead
 * bounces to `/` via `SafeNavigate`, which is client-side, and continues in the same document to a
 * workspace Journeys route that does consume the preload. So skip on `/login` only when no session is
 * persisted. `sessionsStore` is localStorage-backed and readable synchronously, which matters because
 * the preload fires as soon as the shims load, well before the auth bootstrap resolves.
 *
 * Signup is deliberately absent: it renders the embedded app itself and transitions to
 * `post_registration` in-document, so its preload is always used.
 */
export function shouldSkipJourneysPreload({ pathname, hasPersistedSession }: JourneysPreloadSkipContext) {
  if (isUnderRoute(pathname, '/logout')) {
    return true;
  }

  if (isUnderRoute(pathname, '/login')) {
    return !hasPersistedSession;
  }

  return false;
}

declare global {
  interface Window {
    __hydra_preload_app_bundles__?: {
      [key in AppBundle]: Promise<unknown> | undefined;
    };
  }
}

export function getImportShim() {
  if (window.importShim == null) {
    throw new Error('es-module-shims did not initialize window.importShim.');
  }

  return window.importShim;
}

export const registerPreloadPromise = (bundle: AppBundle, callback: () => Promise<unknown>) => {
  window.__hydra_preload_app_bundles__ ??= {
    [AppBundle.Journeys]: undefined,
  };

  if (window.__hydra_preload_app_bundles__[bundle] != null) {
    sentryAddAppLoadBreadcrumb('preload promise already registered', { appBundle: bundle });
    return;
  }

  const startedAt = performance.now();
  sentryAddAppLoadBreadcrumb('preload promise registered', { appBundle: bundle });

  window.__hydra_preload_app_bundles__[bundle] = callback()
    .then((entrypoint) => {
      sentryAddAppLoadBreadcrumb('preload promise resolved', {
        appBundle: bundle,
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      return entrypoint;
    })
    .catch((error: unknown) => {
      sentryAddAppLoadBreadcrumb(
        'preload promise rejected',
        {
          appBundle: bundle,
          elapsedMs: Math.round(performance.now() - startedAt),
        },
        'error',
      );
      sentryCaptureAppLoadDiagnostic(
        'preload_rejected',
        {
          appBundle: bundle,
          elapsedMs: Math.round(performance.now() - startedAt),
          errorName: error instanceof Error ? error.name : typeof error,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
        { level: 'error' },
      );
      throw error;
    });
};

export const getEntrypoint = async (bundle: AppBundle) => {
  const preloadPromise = window.__hydra_preload_app_bundles__?.[bundle];
  if (preloadPromise == null) {
    const startedAt = performance.now();
    sentryAddAppLoadBreadcrumb('entrypoint import started', { appBundle: bundle, source: 'importShim' });

    try {
      const entrypoint = await getImportShim()(bundle);
      sentryAddAppLoadBreadcrumb('entrypoint import resolved', {
        appBundle: bundle,
        source: 'importShim',
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      return entrypoint;
    } catch (error) {
      sentryAddAppLoadBreadcrumb(
        'entrypoint import rejected',
        {
          appBundle: bundle,
          source: 'importShim',
          elapsedMs: Math.round(performance.now() - startedAt),
        },
        'error',
      );
      sentryCaptureAppLoadDiagnostic(
        'entrypoint_import_rejected',
        {
          appBundle: bundle,
          elapsedMs: Math.round(performance.now() - startedAt),
          errorName: error instanceof Error ? error.name : typeof error,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
        { level: 'error' },
      );
      throw error;
    }
  }

  sentryAddAppLoadBreadcrumb('entrypoint awaiting preload promise', { appBundle: bundle });
  return await preloadPromise;
};
